import type { Query, QueryClient } from "@tanstack/vue-query";
import type { AxiosError } from "axios";
import { captureMessage } from "@sentry/vue";
import { isOrganizationMismatchError } from "@/services/queryRetry";
import {
  PROFILE_QUERY_KEY,
  resolveActiveOrganizationId,
  type Profile,
} from "@/services/user";

// The 409 body's `organization`, which names the organization the session is
// actually in. The project's exception handler rewrites an APIException detail
// into {message, code}, so the id arrives as a string under `message`; the
// plain number is accepted too so this does not depend on that handler.
function refusedOrganizationId(error: Error): number | undefined {
  const data = (
    error as AxiosError<{ organization?: number | { message?: string } }>
  ).response?.data;
  const value = data?.organization;
  if (typeof value === "number") return value;
  if (value && typeof value === "object" && value.message !== undefined) {
    const parsed = Number(value.message);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

/**
 * Recovers from the organization-scope 409.
 *
 * Organization-scoped queries name the organization they were keyed for, and
 * the backend refuses with 409 rather than answering as a different one
 * (`utils.organization_scope`). That keeps the wrong organization's data out
 * of the cache, but on its own it only converts a silent corruption into a
 * stuck query: another tab moves the selection, this tab still holds profile
 * A, and every A-keyed request is refused forever. The retry policy stops
 * hammering it; nothing moves the keys.
 *
 * The refusal carries the organization the session is actually in, which is
 * exactly the signal needed: refetch the profile, and every key derived from
 * it re-forms against the real organization and issues the right request.
 *
 * Invalidating rather than writing the id directly, because the profile is
 * the source of truth for far more than the id, and a switch in another tab
 * may have changed the rest of it too.
 */
// Whether a scoped query's key names this organization.
//
// Scanned across the whole key rather than read at a fixed index: the scope
// object sits at [1] for AI usage (`aiTokenUsageKeys`) but at [2] for feature
// flags (`featureFlagsKeys.flags`), and hard-coding [1] silently excluded
// flags from recovery — entitlements stayed unavailable until an unrelated
// refetch or a reload.
function keyedForOrganization(
  queryKey: readonly unknown[],
  organizationId: number,
): boolean {
  return queryKey.some(
    (segment) =>
      typeof segment === "object" &&
      segment !== null &&
      (segment as { organizationId?: unknown }).organizationId ===
        organizationId,
  );
}

export function installOrganizationResync(
  queryClient: QueryClient,
): () => void {
  let resyncing = false;
  const refused = new Map<string, Query>();

  return queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "error") return;
    if (!isOrganizationMismatchError(event.action.error as Error)) return;

    // Every query refused during this burst, not just the one that opened it.
    // A page holds several organization-scoped queries and each gets its own
    // 409; the later ones used to hit the `resyncing` guard below and be
    // forgotten, so only the first was ever retried and the rest stayed
    // failed for good — global retry deliberately stops on this 409.
    refused.set(event.query.queryHash, event.query);

    // One PROFILE refetch per burst, though: N refusals must not become N
    // profile requests. Cleared when the profile settles, so a later genuine
    // mismatch resyncs again rather than being swallowed.
    if (resyncing) return;
    resyncing = true;
    // Exactly what this burst is responsible for, taken before any await.
    // Everything here is deleted on EVERY exit path below; anything added
    // after this line — the mid-retry window — deliberately survives.
    const handled = [...refused.keys()];
    const refusedWith = refusedOrganizationId(event.action.error as Error);
    void queryClient
      .invalidateQueries({ queryKey: PROFILE_QUERY_KEY })
      // `.catch` before the check, not after: a failed refetch REJECTS the
      // invalidate, so a bare `.then` would skip the convergence check
      // entirely and the failure would pass unreported — the quietest version
      // of the dead end this reports.
      .catch(() => undefined)
      .then(async () => {
        // Converging means the refetched profile now resolves to the
        // organization the refusal named — then every derived key re-forms and
        // the right requests go out. When it does NOT, there is no further
        // signal to act on: the keys never move, the retry policy has already
        // stopped, and the queries sit in `error` with nothing on screen to
        // explain it. Report it rather than wedging silently.
        const profile = queryClient.getQueryData<Profile>(PROFILE_QUERY_KEY);
        const resolved = profile
          ? resolveActiveOrganizationId(profile)
          : undefined;
        if (resolved === refusedWith) return;

        // Two situations look identical here — the profile resolves to the
        // organization this query is already keyed for, while the refusal
        // named a different one:
        //
        //   A → B → A: the session moved and came back before the profile
        //     landed. The request is valid again; nothing re-forms because
        //     nothing changed, so the entry just holds a stale refusal.
        //   A genuine divergence: the two sides disagree about the active
        //     organization and will keep disagreeing.
        //
        // They are only distinguishable by trying. Retry once; if it is
        // refused again, THAT is the persistent case worth reporting. This
        // also keeps recovery ahead of diagnostics: the user gets their data
        // back where that is possible, and Sentry only hears about the
        // dead ends that are real.
        // Snapshot, then consume only what we retried. Anything refused AFTER
        // this point — the retry round trip is a real window, not a microtask
        // — survives instead of being cleared unretried, which matters
        // because global retry has already stopped for these.
        const recoverable =
          resolved === undefined
            ? []
            : [...refused.values()].filter((query) =>
                keyedForOrganization(query.queryKey, resolved),
              );
        for (const query of recoverable) refused.delete(query.queryHash);
        if (recoverable.length > 0) {
          await Promise.all(
            recoverable.map((query) =>
              queryClient
                .refetchQueries({ queryKey: query.queryKey, exact: true })
                .catch(() => undefined),
            ),
          );
          // Report only what is STILL refused: a burst can mix a recovered
          // query with a genuinely diverged one.
          //
          // A second refusal re-enters the map above under the same hash.
          // For an entry in this burst's snapshot that is the terminal state:
          // `handled` drops it in `finally`, so it is reported once and not
          // retried in a loop. An entry refused AFTER the snapshot is in
          // `recoverable` but not in `handled`, so it survives like any other
          // mid-retry leftover and a later burst may try it again — bounded
          // by `recoverable`'s organization filter, which stops it being
          // retried once the session has moved on.
          const stillRefused = recoverable.filter((query) =>
            isOrganizationMismatchError(query.state.error as Error),
          );
          if (stillRefused.length === 0) return;
        }

        captureMessage("Organization scope 409 did not converge", {
          extra: {
            refusedWith,
            resolved: resolved ?? "profile refetch returned no data",
          },
        });
      })
      .finally(() => {
        resyncing = false;
        // Drop this burst's entries on every path — including the converged
        // one, which returns before the drain, and a second refusal, which
        // re-enters the map above (the `set` precedes the `resyncing` guard).
        // Without this the map only ever grew, holding Query references for
        // organizations the user had left, and a later burst could refetch
        // them and report a false non-convergence.
        for (const hash of handled) refused.delete(hash);

        // Leftovers refused mid-retry are KEPT, not cleared: `resyncing` is
        // false again, so the next mismatch opens a fresh burst that picks
        // them up along with it. One that the session has since moved past is
        // harmless — `recoverable` filters on the resolved organization, so a
        // stale key is never retried; it only occupies the map.
        //
        // KNOWN GAP: if no further mismatch ever arrives, they are never
        // retried. Closing that needs something to drive the drain — a timer,
        // or an owner that can await it — which does not belong in a cache
        // subscriber: refetching from here outlives the caller (it fires
        // against a torn-down client in tests, and is a request nobody awaits
        // in the app). Tracked as a follow-up rather than bodged here.
      });
  });
}
